import React, { forwardRef, memo, useCallback } from "react";
import PropTypes from "prop-types";
import { InputField } from "./InputField";
import styles from "./TextInput.scss";
import { TextInput } from "./TextInput";
import { useId } from "./useId";
import { AppLogo } from "../misc/AppLogo";
import { Button } from "./Button";
import { FormattedMessage } from "react-intl";
import { upload, parseURL } from "../../utils/media-utils";
import { guessContentType } from "../../utils/media-url-utils";
import configs from "../../utils/configs";

export const LogoInputField = memo(
  forwardRef(
    ({ className, error, description, label, fullWidth, watch, setValue, setError, clearErrors, ...rest }, ref) => {
      const id = useId();
      const labelId = useId();

      const logoFileInput = React.useRef(null);
      const customLogo = watch("logo");
      const onClick = useCallback(
        e => {
          logoFileInput.current.click();
          clearErrors("logo");
        },
        [logoFileInput]
      );

      const onChange = useCallback(
        e => {
          if (e.target.value) {
            const file = e.target.files[0];
            const desiredContentType = file.type || guessContentType(file.name);
            const validImageTypes = ["image/gif", "image/jpeg", "image/png", "image/svg+xml"];
            if (!validImageTypes.includes(desiredContentType)) {
              // invalid file type code goes here.
              console.log("Invalid logo images type", desiredContentType);
              setError("logo", {
                type: "custom",
                message: (
                  <FormattedMessage
                    id="room-sidebar.logo-info.change-logo-type-error"
                    defaultMessage="Change Logo type error"
                  />
                )
              });
              logoFileInput.current.value = "";
              return false;
            }
            upload(file, desiredContentType)
              .then(function (response) {
                const srcUrl = new URL(response.origin);
                srcUrl.searchParams.set("token", response.meta.access_token);
                setValue("logo", srcUrl.href);
                logoFileInput.current.value = "";
                // after upload logo, set logo file is promote (move to owned folder)
                if (window.APP.hubChannel) {
                  window.APP.hubChannel.promote(
                    response.file_id,
                    response.meta.access_token,
                    response.meta.promotion_token
                  );
                }
              })
              .catch(e => {
                console.error("Logo upload failed", e);
                setError("logo", {
                  type: "custom",
                  message: (
                    <FormattedMessage
                      id="room-sidebar.logo-info.change-logo-upload-error"
                      defaultMessage="Change Logo upload error"
                    />
                  )
                });
                logoFileInput.current.value = "";
              });
          }
        },
        [setValue, setError]
      );
      return (
        <InputField
          id={labelId}
          htmlFor={id}
          className={className}
          label={label}
          error={error}
          description={description}
          fullWidth={fullWidth}
        >
          <div className={styles.logoInputFieldContainer}>
            <AppLogo className={styles.logoInputFieldPreview} customLogo={customLogo} />
            <input onChange={onChange} type="file" accept="image/*" style={{ display: "none" }} ref={logoFileInput} />
            <Button onClick={onClick} preset="primary">
              <FormattedMessage id="room-sidebar.logo-info.change-logo-button" defaultMessage="Change Logo" />
            </Button>
            <TextInput id={id} ref={ref} className={styles.hidden} {...rest} />
          </div>
        </InputField>
      );
    }
  )
);

LogoInputField.propTypes = {
  className: PropTypes.string,
  label: PropTypes.node,
  error: PropTypes.node,
  description: PropTypes.node,
  labelClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  fullWidth: PropTypes.bool
};
